import React from "react";
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ExpandMore,
  CheckCircle,
  TipsAndUpdates,
  ThumbUp,
  AutoAwesome,
  Psychology,
  FormatQuote,
} from "@mui/icons-material";
import { EvaluationDetailV2_1_0, ImprovementV2_1, ScoredChecklistItemV2_1 } from "../../../types";
import TopicBadges from "../../common/TopicBadges";

interface Props {
  evaluation: EvaluationDetailV2_1_0;
}

interface ScoreRowProps {
  label: string;
  score: number;
  maxScore: number;
  explanation: string;
}

const ScoreRow: React.FC<ScoreRowProps> = ({ label, score, maxScore, explanation }) => {
  const percentage = (score / maxScore) * 100;
  const ratio = score / maxScore;
  const color: "success" | "primary" | "warning" | "error" =
    ratio >= 0.8 ? "success" : ratio >= 0.6 ? "primary" : ratio >= 0.4 ? "warning" : "error";

  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="body2" fontWeight="medium">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="bold" color={`${color}.main`}>
          {score} / {maxScore}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        color={color}
        sx={{ height: 8, borderRadius: 1 }}
      />
      {explanation && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {explanation}
        </Typography>
      )}
    </Box>
  );
};

interface ScoredChecklistTableProps {
  label: string;
  checklist: ScoredChecklistItemV2_1[] | undefined;
}

const ScoredChecklistTable: React.FC<ScoredChecklistTableProps> = ({ label, checklist }) => {
  if (!checklist || checklist.length === 0) return null;

  const totalPoint = checklist.reduce((sum, checklistItem) => sum + checklistItem.point, 0);
  const totalMaxPoint = checklist.reduce((sum, checklistItem) => sum + checklistItem.maxPoint, 0);
  const deductedItemCount = checklist.filter(
    (checklistItem) => checklistItem.point < checklistItem.maxPoint
  ).length;
  const isFullScore = deductedItemCount === 0;

  return (
    <Box sx={{ mb: 3, "&:last-child": { mb: 0 } }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Typography variant="subtitle2" fontWeight="bold">
          {label}
        </Typography>
        <Chip
          label={isFullScore ? "満点" : `${deductedItemCount}項目で減点`}
          size="small"
          variant="outlined"
          color={isFullScore ? "success" : "warning"}
        />
        <Typography
          variant="body2"
          fontWeight="bold"
          color={isFullScore ? "success.main" : "warning.main"}
          sx={{ ml: "auto" }}
        >
          {totalPoint} / {totalMaxPoint} 点
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>チェック項目</TableCell>
            <TableCell>判定理由</TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              加点
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {checklist.map((checklistItem, index) => (
            <TableRow key={index}>
              <TableCell sx={{ verticalAlign: "top" }}>{checklistItem.item}</TableCell>
              <TableCell sx={{ verticalAlign: "top", color: "text.secondary" }}>
                {checklistItem.reason}
              </TableCell>
              <TableCell align="right" sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={
                    checklistItem.point >= checklistItem.maxPoint
                      ? "success.main"
                      : checklistItem.point > 0
                        ? "warning.main"
                        : "error.main"
                  }
                >
                  {checklistItem.point} / {checklistItem.maxPoint}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const ImprovementItem: React.FC<{ improvement: ImprovementV2_1; index: number }> = ({
  improvement,
  index,
}) => {
  return (
    <Box sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Typography variant="body2" fontWeight="bold" color="info.main">
          {index + 1}.
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          {improvement.title}
        </Typography>
        <Chip
          label={improvement.scope === "partial" ? "部分" : "全体"}
          size="small"
          variant="outlined"
          color={improvement.scope === "partial" ? "info" : "secondary"}
        />
      </Box>
      {improvement.targetQuote && (
        <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ ml: 3, mb: 0.5 }}>
          <FormatQuote fontSize="small" color="disabled" />
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            {improvement.targetQuote}
          </Typography>
        </Box>
      )}
      <Typography variant="body2" sx={{ ml: 3, whiteSpace: "pre-wrap" }}>
        {improvement.explanation}
      </Typography>
      {improvement.rewriteExample && (
        <Paper variant="outlined" sx={{ ml: 3, mt: 1, p: 1, bgcolor: "grey.50" }}>
          <Typography variant="caption" color="text.secondary" display="block">
            書き換え例
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {improvement.rewriteExample}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

const EvaluationDetailV2_1_0View: React.FC<Props> = ({ evaluation }) => {
  const { understanding, logicOriginality, structureExpression, improvement } = evaluation.result;
  const claimTypeForBadge =
    evaluation.topic.claimType == null ? undefined : evaluation.topic.claimType;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3, bgcolor: "grey.50" }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          <Psychology sx={{ mr: 1, verticalAlign: "middle" }} />
          カテゴリ別スコア
        </Typography>
        <ScoreRow
          label="理解度"
          score={understanding.scores.understanding}
          maxScore={25}
          explanation={understanding.scoreExplanations.understanding}
        />
        <ScoreRow
          label="論理性"
          score={logicOriginality.scores.logic}
          maxScore={25}
          explanation={logicOriginality.scoreExplanations.logic}
        />
        <ScoreRow
          label="独創性"
          score={logicOriginality.scores.originality}
          maxScore={20}
          explanation={logicOriginality.scoreExplanations.originality}
        />
        <ScoreRow
          label="構成"
          score={structureExpression.scores.structure}
          maxScore={15}
          explanation={structureExpression.scoreExplanations.structure}
        />
        <ScoreRow
          label="表現力"
          score={structureExpression.scores.expression}
          maxScore={15}
          explanation={structureExpression.scoreExplanations.expression}
        />
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          <AutoAwesome sx={{ mr: 1, verticalAlign: "middle" }} />
          総評
        </Typography>
        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
          {improvement.shortReview}
        </Typography>
      </Paper>

      {improvement.strengths.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "success.50" }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="success.main">
            <ThumbUp sx={{ mr: 1, verticalAlign: "middle" }} />
            良い点
          </Typography>
          <List dense disablePadding>
            {improvement.strengths.map((strength, index) => (
              <ListItem key={index} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={strength} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {improvement.improvements.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "info.50" }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="info.main">
            <TipsAndUpdates sx={{ mr: 1, verticalAlign: "middle" }} />
            改善提案
          </Typography>
          {improvement.improvements.map((improvementItem, index) => (
            <ImprovementItem key={index} improvement={improvementItem} index={index} />
          ))}
        </Paper>
      )}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="bold">
            採点の内訳
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ScoredChecklistTable
            label="理解度"
            checklist={understanding.reasoning?.understandingScoredChecklist}
          />
          <ScoredChecklistTable
            label="論理性"
            checklist={logicOriginality.reasoning?.logicScoredChecklist}
          />
          <ScoredChecklistTable
            label="独創性"
            checklist={logicOriginality.reasoning?.originalityScoredChecklist}
          />
          <ScoredChecklistTable
            label="構成"
            checklist={structureExpression.reasoning?.structureScoredChecklist}
          />
          <ScoredChecklistTable
            label="表現力"
            checklist={structureExpression.reasoning?.expressionScoredChecklist}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              元の問題文
            </Typography>
            <TopicBadges
              difficulty={evaluation.topic.difficulty}
              genre={evaluation.topic.genre}
              questionType={evaluation.topic.questionType}
              claimType={claimTypeForBadge}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {evaluation.topic.theme && (
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {evaluation.topic.theme}
            </Typography>
          )}
          {evaluation.topic.content && (
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {evaluation.topic.content}
            </Typography>
          )}
          {evaluation.topic.background && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                背景説明
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {evaluation.topic.background}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {evaluation.input.understanding && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              ユーザーの理解
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              主張
            </Typography>
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: "grey.50" }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {evaluation.input.understanding.claim || "(未入力)"}
              </Typography>
            </Paper>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              根拠
            </Typography>
            <Paper sx={{ p: 1.5, bgcolor: "grey.50" }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {evaluation.input.understanding.grounds || "(未入力)"}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="bold">
            ユーザーの回答
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {evaluation.input.userOpinion}
            </Typography>
          </Paper>
        </AccordionDetails>
      </Accordion>

      {improvement.improvedExample && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              AI改善例
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper sx={{ p: 2, bgcolor: "primary.50" }}>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {improvement.improvedExample}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default EvaluationDetailV2_1_0View;
